import type { UseQueryResult } from "@tanstack/react-query";
import { Card } from "~/components/Card";
import {
  DetailsLayout,
  HistoryCard,
  ManageCard,
  ResourceFields,
} from "~/components/DetailsCards";
import {
  useDeleteDigestionTopic,
  useDigestionTopic,
} from "~/domain/datastores";
import {
  makeDigestionTopicsLocation,
  makeEditDigestionTopicLocation,
  useDigestionTopicParams,
} from "~/paths";
import type { DigestionTopic } from "~/services/datastore";
import { selectData } from "~/utils";

export function DigestionTopicDetails() {
  const { digestionId, topicId } = useDigestionTopicParams();

  const query = useDigestionTopic(digestionId, topicId, { select: selectData });

  const generalSection = <GeneralSection query={query} />;
  const historySection = <HistoryCard query={query} />;
  const manageSection = (
    <ManageCard
      resourceName="digestion topic"
      query={query}
      editLocation={makeEditDigestionTopicLocation({ digestionId, topicId })}
      deleteMutation={useDeleteDigestionTopic(digestionId, topicId)}
      getReadableName={(digestionTopic) => digestionTopic.id}
      listLocation={makeDigestionTopicsLocation({ digestionId })}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={generalSection}
      secondaryGridColumn={
        <>
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {historySection}
          {manageSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<DigestionTopic> }) {
  return (
    <Card title="General">
      <ResourceFields
        query={query}
        fields={[
          { dataType: "id", accessor: "id" },
          { dataType: "digestion", accessor: "digestionId" },
          { dataType: "topic", accessor: "topicId" },
          { dataType: "timestamp", accessor: "startTime" },
          { dataType: "timestamp", accessor: "endTime" },
          { dataType: "number", accessor: "frequency" },
          { dataType: "json", accessor: "queryDataFilter" },
          { dataType: "json", accessor: "contextFilter" },
        ]}
      />
    </Card>
  );
}
